import './BudgetTable.scss';
import cn from 'classnames';
import { ReactElement } from 'react';
import { BudgetBuilderGroup, BudgetOutcome } from '../budgetBuilderContract';
import {
  formatEfficiencyValue,
  formatOutcomeValue,
  formatSpendValue,
  grainLabel,
  outcomeLabel
} from '../budgetBuilderFormat';
import { derivedEfficiency, pickSeries, resolveIndex, scenarioRows } from '../budgetBuilderMath';
import { InfoTooltip } from '../../../../Components/InfoTooltip';
import { Placement, Size } from '../../../../Components/designConstants';

const CI_TOOLTIP =
  "Confidence interval (CI): the model's likely range for this value at this spend level.";
const SCENARIO_TOOLTIP =
  "Low = lowest modeled weekly spend · Selected = the spend you've set · " +
  'High = highest modeled weekly spend.';

interface BudgetTableProps {
  groups: BudgetBuilderGroup[];
  selectedOutcome: BudgetOutcome;
  /** Per-group selected point index (shared with the card view). */
  selection: Record<string, number>;
}

const ciLine = (
  lower: number | null,
  upper: number | null,
  fmt: (v: number | null) => string
): ReactElement | null =>
  lower != null && upper != null ? (
    <div className='bb-ci'>
      CI: {fmt(Math.min(lower, upper))}–{fmt(Math.max(lower, upper))}
    </div>
  ) : null;

/**
 * Combined table: per spend-group, low / selected / high budget scenarios with
 * outcome and efficiency (each with its CI). Reflects the budget set in Plots
 * view and is downloadable as CSV from the page toolbar.
 */
export const BudgetTable = ({
  groups,
  selectedOutcome,
  selection
}: BudgetTableProps): ReactElement => {
  // Fold the KPI into the outcome header only when every group shares one; the
  // per-row KPI column already disambiguates when they differ.
  const kpis = [...new Set(groups.map(g => g.kpi).filter(Boolean))];
  const outcomeName = outcomeLabel(selectedOutcome, kpis.length === 1 ? kpis[0] : undefined);
  const metricName = selectedOutcome === 'revenue' ? 'ROAS' : 'CPA';

  return (
    <div className='bb-table-wrap'>
      <table className='bb-table' data-testid='budget-table'>
        <thead>
          <tr>
            <th>Channel</th>
            <th>Platform</th>
            <th>Tactic</th>
            <th>Grain</th>
            <th>KPI</th>
            <th>
              <span className='bb-th-label'>
                Scenario
                <InfoTooltip size={Size.SM} placement={Placement.TOP}>
                  {SCENARIO_TOOLTIP}
                </InfoTooltip>
              </span>
            </th>
            <th className='bb-num'>Weekly Spend</th>
            <th className='bb-num'>
              <span className='bb-th-label'>
                Weekly {outcomeName}
                <InfoTooltip size={Size.SM} placement={Placement.TOP}>
                  {CI_TOOLTIP}
                </InfoTooltip>
              </span>
            </th>
            <th className='bb-num'>
              <span className='bb-th-label'>
                Weekly {metricName}
                <InfoTooltip size={Size.SM} placement={Placement.TOP}>
                  {CI_TOOLTIP}
                </InfoTooltip>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((g, gi) => {
            const series = pickSeries(g, selectedOutcome);
            const sel = resolveIndex(selection, g.id, series.curve.length);
            const scenarios = scenarioRows(series.curve.length, sel);
            // Shade whole group-blocks (all scenario rows share the group's
            // parity) so the striping reads as one row per spend-group.
            const groupAlt = gi % 2 === 1;
            return scenarios.map((s, i) => {
              const p = series.curve[s.index];
              const eff = derivedEfficiency(series.efficiencyMetric, p);
              const className =
                cn({ 'bb-group-start': i === 0, 'bb-group-alt': groupAlt }) || undefined;
              return (
                <tr key={g.id + s.label} className={className}>
                  {i === 0 && (
                    <>
                      <td rowSpan={scenarios.length}>{g.channel}</td>
                      <td rowSpan={scenarios.length}>{g.platform || '—'}</td>
                      <td rowSpan={scenarios.length}>{g.tactic || '—'}</td>
                      <td rowSpan={scenarios.length}>
                        {grainLabel(g.channel, g.platform, g.tactic)}
                      </td>
                      <td rowSpan={scenarios.length}>{g.kpi}</td>
                    </>
                  )}
                  <td>{s.label}</td>
                  <td className='bb-num'>{formatSpendValue(p.spend)}</td>
                  <td className='bb-num'>
                    <div>{formatOutcomeValue(series.outcome, p.volume)}</div>
                    {ciLine(p.volumeLower, p.volumeUpper, v =>
                      formatOutcomeValue(series.outcome, v)
                    )}
                  </td>
                  <td className='bb-num'>
                    <div>{formatEfficiencyValue(series.efficiencyMetric, eff.value)}</div>
                    {ciLine(eff.lower, eff.upper, v =>
                      formatEfficiencyValue(series.efficiencyMetric, v)
                    )}
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
};
